import { DateType, RankMethod, RankMethodType, xLabel } from '@/pages/EnergyAnalysisRanking/utils';
import { Button, EllipsisSpan, FieldTable, Paging, Tag, Tooltip, UsePagingProps } from '@maxtropy/components';
import { V2EnergyUnitConsumptionRankingPostResponse } from '@maxtropy/device-customer-apis-v2';
import { Space } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import ShowDetailModal from './ShowDetailModal';
import { keepPercentOrLine, keepTwoNull } from '@/utils/utils';
import { MeterType } from '@/api/energyConsumption';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { CustomerRequest } from '../..';

export interface IRankTable {
  data?: V2EnergyUnitConsumptionRankingPostResponse['list'];
  loading?: boolean;
  rankMethod: RankMethod;
  customerRequest?: CustomerRequest;
  timeResolution?: DateType;
  energyMethod: RankMethodType;
  pagingInfo: UsePagingProps;
  selectedConsumption?: number;
  energyUnitGroupId?: number;
  displayValue?: dayjs.Dayjs;
  dataQueryPeriodId?: number;
  dataQueryPeriodName?: string;
}

export type listItemProps = Exclude<V2EnergyUnitConsumptionRankingPostResponse['list'], undefined>[number];

const RankTable: FC<IRankTable> = props => {
  const {
    data,
    loading,
    rankMethod,
    customerRequest,
    timeResolution,
    energyMethod,
    pagingInfo,
    selectedConsumption,
    energyUnitGroupId,
    displayValue,
    dataQueryPeriodId,
    dataQueryPeriodName,
  } = props;

  const [openModal, setOpenModal] = useState(false); // 新建配置弹窗
  const [recordId, setRecordId] = useState<number>(); // 每行数据的id

  const renderUseEnergy = useCallback(
    (record: listItemProps, type: 'total' | 'periodTotal' | 'totalIncrement' | 'totalIncrementRate') => {
      let total = keepTwoNull(record[type]);
      let summit = keepTwoNull(record.summit);
      let peak = keepTwoNull(record.peak);
      let plain = keepTwoNull(record.plain);
      let valley = keepTwoNull(record.valley);

      switch (type) {
        case 'periodTotal':
          total = keepTwoNull(record.periodTotal);
          summit = keepTwoNull(record.periodSummitElectricity);
          peak = keepTwoNull(record.periodPeakElectricity);
          plain = keepTwoNull(record.periodPlainElectricity);
          valley = keepTwoNull(record.periodValleyElectricity);
          break;
        case 'totalIncrement':
          total = keepTwoNull(record.totalIncrement);
          summit = keepTwoNull(record.summitIncrement);
          peak = keepTwoNull(record.peakIncrement);
          plain = keepTwoNull(record.plainIncrement);
          valley = keepTwoNull(record.valleyIncrement);
          break;
        case 'totalIncrementRate':
          total = keepPercentOrLine(record.totalIncrementRate) + '%';
          summit = keepPercentOrLine(record.summitIncrementRate) + '%';
          peak = keepPercentOrLine(record.peakIncrementRate) + '%';
          plain = keepPercentOrLine(record.plainIncrementRate) + '%';
          valley = keepPercentOrLine(record.valleyIncrementRate) + '%';
          break;
      }

      switch (energyMethod) {
        case RankMethodType.ALL:
          if (selectedConsumption === MeterType.ELECTRICAL_ENERGY) {
            return (
              <Tooltip
                title={
                  <>
                    <span>总：{total}</span>
                    <br />
                    <span>尖：{summit}</span>
                    <br />
                    <span>峰：{peak}</span>
                    <br />
                    <span>平：{plain}</span>
                    <br />
                    <span>谷：{valley}</span>
                  </>
                }
              >
                <Tag border="solid" color="default">
                  总：{total}
                </Tag>
                <Tag border="solid" color="#FF9247">
                  尖：{summit}
                </Tag>
                <Tag border="solid" color="#CE90D1">
                  峰：{peak}
                </Tag>
                <Tag border="solid" color="#52E7FF">
                  平：{plain}
                </Tag>
                <Tag border="solid" color="#57FB8B">
                  谷：{valley}
                </Tag>
              </Tooltip>
            );
          } else {
            return (
              <Tooltip title={<span>总：{total}</span>}>
                <Tag border="solid" color="default">
                  总：{total}
                </Tag>
              </Tooltip>
            );
          }
        case RankMethodType.SHARP:
          return (
            <Tooltip title={<span>尖：{summit}</span>}>
              <Tag border="solid" color="#FF9247">
                尖：{summit}
              </Tag>
            </Tooltip>
          );
        case RankMethodType.PEAK:
          return (
            <Tooltip title={<span>峰：{peak}</span>}>
              <Tag border="solid" color="#CE90D1">
                峰：{peak}
              </Tag>
            </Tooltip>
          );
        case RankMethodType.AVERAGE:
          return (
            <Tooltip title={<span>平：{plain}</span>}>
              <Tag border="solid" color="#52E7FF">
                平：{plain}
              </Tag>
            </Tooltip>
          );
        case RankMethodType.VALLEY:
          return (
            <Tooltip title={<span>谷：{valley}</span>}>
              <Tag border="solid" color="#57FB8B">
                谷：{valley}
              </Tag>
            </Tooltip>
          );

        default:
          break;
      }
    },
    [energyMethod, selectedConsumption]
  );

  const columns = useMemo(() => {
    const { currentPeriod, comparisonPeriod, timeResolution: customTimeResolution } = customerRequest || {};
    return [
      {
        title: '排名',
        width: 100,
        fixed: 'left' as const,
        dataIndex: 'rankIndex',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '用能单元',
        width: 160,
        fixed: 'left' as const,
        dataIndex: 'energyUnitName',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: `本期用量（${data?.[0]?.unit ?? '-'}）${
          timeResolution === DateType.CUSTOM && !isEmpty(customerRequest)
            ? `-${dayjs(currentPeriod?.[0]).format(xLabel(customTimeResolution, currentPeriod?.[0]))}-${dayjs(
                currentPeriod?.[1]
              ).format(xLabel(customTimeResolution, currentPeriod?.[1]))}`
            : ''
        }`,
        dataIndex: 'total',
        width: 320,
        ellipsis: { showTitle: true },
        render: (v: number, record: listItemProps) => renderUseEnergy(record, 'total'),
      },
      {
        dataIndex: 'total',
        ellipsis: { showTitle: true },
        width: 320,
        title: `上期用量（${data?.[0]?.unit ?? '-'}）`,
        hidden: !(
          rankMethod === RankMethod.BY_INCREMENT &&
          (timeResolution !== DateType.CUSTOM || (timeResolution === DateType.CUSTOM && isEmpty(customerRequest)))
        ),
        render: (v: number, record: listItemProps) => renderUseEnergy(record, 'periodTotal'),
      },
      {
        dataIndex: 'total',
        ellipsis: { showTitle: true },
        width: 320,
        hidden: !(
          rankMethod === RankMethod.BY_INCREMENT &&
          timeResolution === DateType.CUSTOM &&
          !isEmpty(customerRequest)
        ),
        title: `对比周期用量（${data?.[0]?.unit ?? '-'}）${
          timeResolution === DateType.CUSTOM
            ? `-${dayjs(comparisonPeriod?.[0]).format(xLabel(customTimeResolution, comparisonPeriod?.[0]))}-${dayjs(
                comparisonPeriod?.[1]
              ).format(xLabel(customTimeResolution, comparisonPeriod?.[1]))}`
            : ''
        }`,
        render: (v: number, record: listItemProps) => renderUseEnergy(record, 'periodTotal'),
      },
      {
        dataIndex: 'total',
        ellipsis: { showTitle: true },
        width: 320,
        hidden: rankMethod !== RankMethod.BY_INCREMENT,
        title: `用能增量（${data?.[0]?.unit ?? '-'}）`,
        render: (v: number, record: listItemProps) => renderUseEnergy(record, 'totalIncrement'),
      },
      {
        dataIndex: 'totalIncrementRate',
        ellipsis: { showTitle: true },
        width: 120,
        hidden: rankMethod !== RankMethod.BY_INCREMENT,
        title: `用能增幅`,
        render: (v: number, record: listItemProps) => renderUseEnergy(record, 'totalIncrementRate'),
      },
    ];
  }, [energyMethod, selectedConsumption, data, customerRequest]);

  // 表格操作栏
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      fixed: 'right' as const,
      width: 150,
      render: (record: listItemProps) => (
        <Space size={16}>
          <Button
            type="link"
            onClick={() => {
              setOpenModal(true);
              setRecordId(record.energyUnitId);
            }}
          >
            查看用能详情
          </Button>
        </Space>
      ),
    },
  ];
  return (
    <>
      <FieldTable
        rowKey="energyUnitId"
        sticky
        cacheKey="rank-table"
        loading={loading}
        columns={buildColumns}
        dataSource={data}
        pagination={false}
        adjustWidth={true}
      />
      <Paging pagingInfo={pagingInfo} />
      {openModal && (
        <ShowDetailModal
          timeResolution={timeResolution}
          displayValue={displayValue}
          dataQueryPeriodId={dataQueryPeriodId}
          dataQueryPeriodName={dataQueryPeriodName}
          id={recordId}
          selectedConsumption={selectedConsumption}
          energyUnitGroupId={energyUnitGroupId}
          customerRequest={customerRequest}
          onCancel={() => {
            setOpenModal(false);
            setRecordId(undefined);
          }}
        />
      )}
    </>
  );
};

export default RankTable;
